import { getToken, clearToken } from '../utils/authToken';
/**
 * DamyxNotifications - Sistema de Notificaciones Push Nativas
 * Permite a DamyxIA enviar notificaciones push al navegador del usuario.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, BellRing, Check, X, Settings, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

import { API_URL } from '../config';

// Colores del sistema de diseño
const COLORS = {
  background: '#0F172A',
  panel: '#1E293B',
  border: '#334155',
  textPrimary: '#FFFFFF',
  textSecondary: '#CBD5E1',
  primary: '#3B82F6',
  success: '#22C55E',
  warning: '#EAB308',
  error: '#EF4444',
};

// VAPID Public Key para Web Push (generada para DamyxIA)
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

// Convertir Base64 URL a Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Hook para manejar notificaciones push
export const usePushNotifications = () => {
  const { language } = useLanguage();
  const es = language === 'es';
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Verificar soporte del navegador
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        // Verificar si ya hay una subscripción activa
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          setSubscription(existingSubscription);
        } catch (error) {
          console.error('Error checking existing subscription:', error);
        }
      }
    };
    
    checkSupport();
  }, []);

  // Solicitar permiso y subscribirse
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Tu navegador no soporta notificaciones push');
      return null;
    }

    setIsLoading(true);
    
    try {
      // Solicitar permiso
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Permiso de notificaciones denegado');
        setIsLoading(false);
        return null;
      }

      // Registrar service worker si no existe
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }

      // Crear subscripción push
      const newSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      setSubscription(newSubscription);
      
      // Enviar subscripción al backend
      const token = getToken();
      if (token) {
        await axios.post(`${API_URL}/api/notifications/subscribe`, {
          subscription: newSubscription.toJSON()
        }, {
        });
      }

      toast.success(es ? 'Notificaciones activadas correctamente' : 'Notifications enabled successfully');
      setIsLoading(false);
      return newSubscription;
      
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Error al activar notificaciones');
      setIsLoading(false);
      return null;
    }
  }, [isSupported]);

  // Cancelar subscripción
  const unsubscribe = useCallback(async () => {
    if (!subscription) return;

    setIsLoading(true);
    
    try {
      await subscription.unsubscribe();
      setSubscription(null);
      
      // Notificar al backend
      const token = getToken();
      if (token) {
        await axios.post(`${API_URL}/api/notifications/unsubscribe`, {}, {
        });
      }

      toast.success(es ? 'Notificaciones desactivadas' : 'Notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error(es ? 'Error al desactivar notificaciones' : 'Error disabling notifications');
    }
    
    setIsLoading(false);
  }, [subscription]);

  // Enviar notificación de prueba
  const sendTestNotification = useCallback(async () => {
    const token = getToken();
    if (!token) {
      toast.error(es ? 'Debes iniciar sesión' : 'You must log in');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/notifications/test`, {}, {
      });
      toast.success(es ? 'Notificación de prueba enviada' : 'Test notification sent');
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error(es ? 'Error al enviar notificación de prueba' : 'Error sending test notification');
    }
  }, []);

  return {
    isSupported,
    permission,
    subscription,
    isLoading,
    subscribe,
    unsubscribe,
    sendTestNotification,
    isSubscribed: !!subscription
  };
};

// Componente de configuración de notificaciones
export const NotificationSettings = ({ compact = false }) => {
  const { language } = useLanguage();
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  const isSpanish = language === 'es';

  if (!isSupported) {
    return compact ? null : (
      <div 
        className="rounded-xl p-4 border"
        style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-3 text-zinc-500">
          <BellOff className="w-5 h-5" />
          <span className="text-sm">
            {isSpanish 
              ? 'Tu navegador no soporta notificaciones push' 
              : 'Your browser does not support push notifications'}
          </span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={isSubscribed ? unsubscribe : subscribe}
        disabled={isLoading}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
          isSubscribed 
            ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
            : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
        }`}
        data-testid="notification-toggle"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : isSubscribed ? (
          <BellRing className="w-4 h-4" />
        ) : (
          <Bell className="w-4 h-4" />
        )}
        <span className="text-sm">
          {isSubscribed 
            ? (isSpanish ? 'Notificaciones ON' : 'Notifications ON')
            : (isSpanish ? 'Activar' : 'Enable')}
        </span>
      </button>
    );
  }

  return (
    <div 
      className="rounded-xl overflow-hidden border"
      style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}
      data-testid="notification-settings"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: COLORS.border }}
      >
        <div className="flex items-center gap-3">
          <div 
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isSubscribed ? 'bg-emerald-500/20' : 'bg-zinc-700'
            }`}
          >
            {isSubscribed ? (
              <BellRing className="w-5 h-5 text-emerald-400" />
            ) : (
              <Bell className="w-5 h-5 text-zinc-400" />
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">
              {isSpanish ? 'Notificaciones Push' : 'Push Notifications'}
            </h3>
            <p className="text-xs text-zinc-500">
              {isSubscribed 
                ? (isSpanish ? 'Activadas' : 'Enabled')
                : (isSpanish ? 'Desactivadas' : 'Disabled')}
            </p>
          </div>
        </div>

        <div 
          className={`w-12 h-6 rounded-full relative cursor-pointer transition-colors ${
            isSubscribed ? 'bg-emerald-500' : 'bg-zinc-600'
          }`}
          onClick={isSubscribed ? unsubscribe : subscribe}
        >
          <div 
            className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${
              isSubscribed ? 'translate-x-6' : 'translate-x-0.5'
            }`}
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {permission === 'denied' && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-400 font-medium">
                {isSpanish ? 'Permisos denegados' : 'Permission denied'}
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                {isSpanish 
                  ? 'Debes habilitar las notificaciones en la configuración de tu navegador.' 
                  : 'You need to enable notifications in your browser settings.'}
              </p>
            </div>
          </div>
        )}

        <p className="text-sm text-zinc-400">
          {isSpanish 
            ? 'Recibe notificaciones cuando DamyxIA tenga actualizaciones importantes o cuando tus tareas estén listas.' 
            : 'Receive notifications when DamyxIA has important updates or when your tasks are ready.'}
        </p>

        {isSubscribed && (
          <button
            onClick={sendTestNotification}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white text-sm transition-colors"
            data-testid="test-notification-btn"
          >
            <Bell className="w-4 h-4" />
            {isSpanish ? 'Enviar notificación de prueba' : 'Send test notification'}
          </button>
        )}
      </div>
    </div>
  );
};

// Función para mostrar notificación local (sin push server)
export const showLocalNotification = async (title, options = {}) => {
  if (!('Notification' in window)) {
    console.warn('Notifications not supported');
    return;
  }

  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      vibrate: [200, 100, 200],
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    return notification;
  } else if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      return showLocalNotification(title, options);
    }
  }
};

// Tipos de notificaciones de DamyxIA
export const NOTIFICATION_TYPES = {
  TASK_COMPLETE: 'task_complete',
  NEW_MESSAGE: 'new_message',
  SYSTEM_UPDATE: 'system_update',
  REMINDER: 'reminder'
};

// Templates de notificaciones
export const notificationTemplates = {
  [NOTIFICATION_TYPES.TASK_COMPLETE]: {
    icon: '✅',
    getTitle: (lang) => lang === 'es' ? 'Tarea completada' : 'Task completed',
    getBody: (data, lang) => lang === 'es' 
      ? `Tu tarea "${data.taskName}" ha sido completada.`
      : `Your task "${data.taskName}" has been completed.`
  },
  [NOTIFICATION_TYPES.NEW_MESSAGE]: {
    icon: '💬',
    getTitle: (lang) => lang === 'es' ? 'Nuevo mensaje de DamyxIA' : 'New message from DamyxIA',
    getBody: (data, lang) => data.preview || (lang === 'es' ? 'Tienes un nuevo mensaje' : 'You have a new message')
  },
  [NOTIFICATION_TYPES.SYSTEM_UPDATE]: {
    icon: '🔔',
    getTitle: (lang) => lang === 'es' ? 'Actualización de DamyxIA' : 'DamyxIA Update',
    getBody: (data, lang) => data.message || ''
  },
  [NOTIFICATION_TYPES.REMINDER]: {
    icon: '⏰',
    getTitle: (lang) => lang === 'es' ? 'Recordatorio' : 'Reminder',
    getBody: (data, lang) => data.message || ''
  }
};

export default {
  usePushNotifications,
  NotificationSettings,
  showLocalNotification,
  NOTIFICATION_TYPES,
  notificationTemplates
};
